// Ready, heating or cooling for the screens, as the round display says it (core/status.ts tempTone): the ring of the
// tab "Maschine", the state chip over every other tab and the temperature row of the shot dial use these, with the
// hold the store keeps per machine (MachineView.readyHeld).
import { findCommand } from '@brewcore/protocol';
import { liveOf, readyTarget, READY_BAND_K, tempTone, type Live, type TempTone } from '../status.js';
import type { ScreenContext } from './context.js';

/** The machine's ready band (display.blinking.delta) as the display uses it. */
export function readyBand(ctx: ScreenContext): number {
  const band = ctx.value('display.blinking.delta');
  return typeof band === 'number' && band > 0 ? band : READY_BAND_K;
}

/** The machine's own value, not the one −/+ is collecting (MachineStore.machineValue). */
function machineNumber(ctx: ScreenContext, id: string): number | undefined {
  const group = findCommand(ctx.catalog, id)?.group;
  const values = group
    ? (ctx.view.groups[group]?.data.values as Record<string, unknown> | undefined)
    : undefined;
  const v = values?.[id];
  return typeof v === 'number' ? v : undefined;
}

/** What the temperature is compared with (core/status.ts readyTarget), the machine's setpoint by default. */
export function compareTarget(
  ctx: ScreenContext,
  live: Live | null,
  standby: boolean,
  machineSetpoint = machineNumber(ctx, 'brew.setpoint'),
): number | undefined {
  return readyTarget(live, standby, machineSetpoint, ctx.num('standby.warm_temp'));
}

/** Ready, heating or cooling now (with the display's hold); null without a live temperature or a target. */
export function readyTone(ctx: ScreenContext, temp?: number, target?: number): TempTone | null {
  const live = liveOf(ctx.view, ctx.now);
  const t = temp ?? (typeof live?.currentTemp === 'number' ? live.currentTemp : undefined);
  const aim = target ?? compareTarget(ctx, live, false);
  if (t === undefined || aim === undefined) return null;
  return tempTone(t, aim, readyBand(ctx), ctx.view.readyHeld ?? false);
}
